import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface CartItem {
  id: string;
  offering_id: string;
  name: string;
  price: number;
  currency: string;
  quantity: number;
  image_url?: string;
}

export default function Checkout() {
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [placingOrder, setPlacingOrder] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    loadCart();
    // Check if we returned from Paystack with a reference
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('reference');
    if (ref) {
      verifyPayment(ref);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  async function loadCart() {
    try {
      setLoading(true);
      const res = await api.get('/api/checkout/cart');
      setItems(res.data?.items ?? []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function updateQty(itemId: string, quantity: number) {
    if (quantity <= 0) {
      await api.del(`/api/checkout/cart/${itemId}`);
    } else {
      await api.put(`/api/checkout/cart/${itemId}`, { quantity });
    }
    loadCart();
  }

  async function clearCart() {
    await api.del('/api/checkout/cart');
    setItems([]);
  }

  async function placeOrder() {
    if (items.length === 0) return;
    setPlacingOrder(true);
    try {
      const res = await api.post('/api/checkout/order', {
        customer_name: customerName || undefined,
        customer_email: customerEmail || undefined,
      });
      alert(`Order placed! Order ID: ${res.data.order_id}`);
      setItems([]);
    } catch (err: any) {
      setError(err.message || 'Failed to place order');
    } finally {
      setPlacingOrder(false);
    }
  }

  async function payWithPaystack() {
    if (items.length === 0) return;
    if (!customerEmail) {
      setError('Email is required for Paystack payment');
      return;
    }
    setPaying(true);
    try {
      // First create an order
      const orderRes = await api.post('/api/checkout/order', {
        customer_name: customerName || undefined,
        customer_email: customerEmail,
      });
      const orderId = orderRes.data.order_id;

      // Initiate Paystack
      const payRes = await api.post('/api/payments/initiate', {
        order_id: orderId,
        email: customerEmail,
        callback_url: `${window.location.origin}/checkout`,
      });

      // Redirect to Paystack
      window.location.href = payRes.data.authorization_url;
    } catch (err: any) {
      setError(err.message || 'Payment initiation failed');
      setPaying(false);
    }
  }

  async function verifyPayment(reference: string) {
    setLoading(true);
    try {
      const res = await api.get(`/api/payments/verify/${reference}`);
      if (res.data?.paid) {
        setPaid(true);
        setItems([]);
      } else {
        setError('Payment was not successful. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Payment verification failed');
    } finally {
      setLoading(false);
    }
  }

  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const currency = items[0]?.currency ?? 'NGN';

  if (loading) return <div style={{ padding: 40 }}>Loading...</div>;

  if (paid) {
    return (
      <div style={{ maxWidth: 720, textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 8 }}>Payment Successful</h1>
        <p style={{ color: '#6b7280' }}>Thank you! Your order has been paid and confirmed.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <h1 style={styles.heading}>Checkout</h1>
      {error && <div style={styles.error}>{error}</div>}

      {items.length === 0 ? (
        <div style={styles.empty}>Your cart is empty.</div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ color: '#6b7280', fontSize: 14 }}>{items.length} item(s)</span>
            <button onClick={clearCart} style={styles.linkBtn}>Clear cart</button>
          </div>

          <div style={styles.list}>
            {items.map((item) => (
              <div key={item.id} style={styles.item}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{item.name}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    {item.currency} {item.price.toFixed(2)} each
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button style={styles.qtyBtn} onClick={() => updateQty(item.id, item.quantity - 1)}>−</button>
                  <span style={{ minWidth: 24, textAlign: 'center', fontSize: 14 }}>{item.quantity}</span>
                  <button style={styles.qtyBtn} onClick={() => updateQty(item.id, item.quantity + 1)}>+</button>
                </div>
                <div style={{ minWidth: 80, textAlign: 'right', fontWeight: 500 }}>
                  {item.currency} {(item.price * item.quantity).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          <div style={styles.summary}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
              <span>Total</span>
              <span>{currency} {total.toFixed(2)}</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              <input
                type="text"
                placeholder="Customer name (optional)"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                style={styles.input}
              />
              <input
                type="email"
                placeholder="Customer email (required for card payment)"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                style={styles.input}
              />
            </div>

            <button onClick={placeOrder} disabled={placingOrder} style={{ ...styles.payBtn, marginBottom: 10, background: '#374151' }}>
              {placingOrder ? 'Placing order...' : `Place Order (Cash/Invoice) — ${currency} ${total.toFixed(2)}`}
            </button>
            <button onClick={payWithPaystack} disabled={paying} style={{ ...styles.payBtn, background: '#10b981' }}>
              {paying ? 'Redirecting to Paystack...' : `Pay with Paystack — ${currency} ${total.toFixed(2)}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  heading: { fontSize: 20, fontWeight: 600, marginBottom: 16 },
  error: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 },
  empty: { textAlign: 'center', color: '#9ca3af', padding: 60, fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  item: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' },
  qtyBtn: { width: 28, height: 28, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 16 },
  summary: { marginTop: 24, padding: 20, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' },
  input: { padding: '10px 12px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 8, fontFamily: 'inherit' },
  payBtn: { width: '100%', padding: 12, fontSize: 15, fontWeight: 600, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' },
  linkBtn: { background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' },
};
